/** Shared shaping for the barista Excel exports. Money is JOD fils (3 decimals). */

export type ExportOrder = {
  id?: string;
  order_number?: string;
  cafe_id?: string;
  cafe_name?: string;
  user_email?: string;
  items?: Array<{ quantity?: number; name?: string; menu_item_id?: string }>;
  status?: string;
  created_at?: string | null;
  payment_method?: string | null;
  total_amount?: number | null;
  wallet_used?: number | null;
  points_discount_jod?: number | null;
  points_used?: number | null;
  points_earned?: number | null;
  promotion_discount?: number | null;
  student_discount?: number | null;
  merchandise_subtotal_jod?: number | null;
  platform_fee_jod?: number | null;
  jordan_tax_jod?: number | null;
  service_fee_jod?: number | null;
  small_order_fee_jod?: number | null;
  delivery_fee_jod?: number | null;
  fulfillment_type?: string | null;
  dine_in_discount_jod?: number | null;
  dine_in_commission_jod?: number | null;
  refunded_jod?: number | null;
};

export function roundJod(n: number): number {
  return Math.round(Number(n || 0) * 1000) / 1000;
}

export function ammanYmd(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Amman',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatAmmanDateTime(value?: string | null): string {
  const d = new Date(value || '');
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Amman',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

function paidByCard(order: ExportOrder): boolean {
  const method = String(order.payment_method || '').toLowerCase().trim();
  return method === 'visa' || method === 'apple_pay' || method === 'google_pay';
}

/** Cancellation refund: only money that was actually collected. Unpaid cash is zero. */
export function cancellationRefund(order: ExportOrder) {
  const wallet = roundJod(Number(order.wallet_used || 0));
  const pointsJod = roundJod(Number(order.points_discount_jod || 0));
  const cardPaid = paidByCard(order) ? roundJod(Number(order.total_amount || 0)) : 0;
  const merch = roundJod(Number(order.merchandise_subtotal_jod || 0));
  const platformFee = roundJod(Number(order.platform_fee_jod || 0));
  let merchant = 0;
  let platformShare = 0;
  if (cardPaid > 0) {
    const cafeGoods = Math.max(0, roundJod(merch - platformFee));
    merchant = Math.min(cardPaid, cafeGoods);
    platformShare = roundJod(Math.max(0, cardPaid - merchant));
  }
  return {
    customer_refund_jod: cardPaid,
    wallet_credit_jod: wallet,
    points_discount_jod: pointsJod,
    merchant_charge_jod: merchant,
    qahwa_hub_portion_jod: platformShare,
    payout_adjustment_note: paidByCard(order)
      ? 'Customer refund is the card charge only. Wallet credit and points are restored separately and are not added on top.'
      : 'Cash was not collected, so there is no customer refund. Wallet credit and points are listed separately and are not cash.',
  };
}

export function netAfterRefund(order: ExportOrder): number {
  return roundJod(Math.max(0, Number(order.total_amount || 0) - Number(order.refunded_jod || 0)));
}

export function paymentDetailRow(order: ExportOrder, cafeName: string) {
  return {
    order_number: order.order_number || (order.id || '').slice(0, 8),
    created_at_amman: formatAmmanDateTime(order.created_at),
    cafe: cafeName,
    payment_method: order.payment_method || '',
    fulfillment: order.fulfillment_type || 'pickup',
    total_jod: roundJod(Number(order.total_amount || 0)),
    refunded_jod: roundJod(Number(order.refunded_jod || 0)),
    net_after_refund_jod: netAfterRefund(order),
    delivery_fee_jod: roundJod(Number(order.delivery_fee_jod || 0)),
    student_discount_jod: roundJod(Number(order.student_discount || 0)),
    points_discount_jod: roundJod(Number(order.points_discount_jod || 0)),
    jordan_tax_jod: roundJod(Number(order.jordan_tax_jod || 0)),
    service_fee_jod: roundJod(Number(order.service_fee_jod || 0)),
    small_order_fee_jod: roundJod(Number(order.small_order_fee_jod || 0)),
    wallet_used_jod: roundJod(Number(order.wallet_used || 0)),
    promotion_discount_jod: roundJod(Number(order.promotion_discount || 0)),
    dine_in_discount_jod: roundJod(Number(order.dine_in_discount_jod || 0)),
    dine_in_commission_recorded_jod: roundJod(Number(order.dine_in_commission_jod || 0)),
    points_earned: order.points_earned ?? 0,
    status: order.status || '',
  };
}

export function orderHistoryRow(
  order: ExportOrder,
  cafeName: string,
  items: string
) {
  return {
    order_number: order.order_number || (order.id || '').slice(0, 8),
    created_at_amman: formatAmmanDateTime(order.created_at),
    status: order.status || '',
    cafe: cafeName,
    payment_method: order.payment_method || '',
    fulfillment: order.fulfillment_type || 'pickup',
    total_jod: roundJod(Number(order.total_amount || 0)),
    refunded_jod: roundJod(Number(order.refunded_jod || 0)),
    net_after_refund_jod: netAfterRefund(order),
    delivery_fee_jod: roundJod(Number(order.delivery_fee_jod || 0)),
    student_discount_jod: roundJod(Number(order.student_discount || 0)),
    points_discount_jod: roundJod(Number(order.points_discount_jod || 0)),
    jordan_tax_jod: roundJod(Number(order.jordan_tax_jod || 0)),
    service_fee_jod: roundJod(Number(order.service_fee_jod || 0)),
    dine_in_discount_jod: roundJod(Number(order.dine_in_discount_jod || 0)),
    dine_in_commission_recorded_jod: roundJod(Number(order.dine_in_commission_jod || 0)),
    items,
  };
}
